import { useEffect } from "react";
import { CookiesProvider } from "react-cookie";
import { RouterProvider } from "react-router";
import { setupNewRelic } from "@/shared/telemetry";
import { setupPageTitleElement } from "@/lib/external-script-compat";

import { useSeonSession } from "@/services/fraud-detection";
import { CosmosProvider } from "@/services/cosmos";
import { StatsigProviderWithSession, StatsigReplayErrorBoundary } from "@/shared/abt";
import { logWarn } from "@/services/logging/logger";
import * as events from "@/events";
import { emitPageLoadTracking } from "@/shared/tracking/analytics";
import {
  getCheckoutSeonCookie,
  setCheckoutSeonCookie,
  getCheckoutToken,
  UNDEFINED_CHECKOUT_ID,
  UNDEFINED_CHECKOUT_SEON,
  UNDEFINED_CHECKOUT_TOKEN,
} from "@/services/cookie";
import { initCheckoutId } from "@/lib/checkout-id";

import { router } from "./router";

const resolvedCheckoutId = initCheckoutId();

const initialCheckoutId = resolvedCheckoutId ?? UNDEFINED_CHECKOUT_ID;
if (initialCheckoutId === UNDEFINED_CHECKOUT_ID) {
  logWarn("checkout", "App running without checkout ID");
}
const initialCheckoutToken =
  getCheckoutToken() || UNDEFINED_CHECKOUT_TOKEN;

// DON'T fallback to UNDEFINED_CHECKOUT_SEON,
// we would let the `useSeonSession` generate a new one if we didn't receive from cookie.
// This ensures we always have a valid Seon session.
const initialCheckoutSeon = getCheckoutSeonCookie();

function App() {
  const { data: checkoutSeon } = useSeonSession(initialCheckoutSeon);

  if (!initialCheckoutSeon && checkoutSeon) {
    setCheckoutSeonCookie(checkoutSeon);
  }

  useEffect(() => {
    const removeEvents = setupNewRelic();
    const cleanupPageTitle = setupPageTitleElement();

    return () => {
      removeEvents();
      cleanupPageTitle();
    };
  }, []);

  useEffect(() => {
    emitPageLoadTracking();
  }, []);

  // eslint-disable-next-line react-hooks/immutability, @typescript-eslint/no-explicit-any
  (window as any).__CHECKOUT_EVENTS__ = events.ALL_EVENTS;

  return (
    <CosmosProvider
      checkoutId={initialCheckoutId}
      checkoutToken={initialCheckoutToken}
      checkoutSeon={checkoutSeon ?? UNDEFINED_CHECKOUT_SEON}
    >
      <StatsigProviderWithSession>
        <StatsigReplayErrorBoundary>
          <CookiesProvider>
            <RouterProvider router={router} />
          </CookiesProvider>
        </StatsigReplayErrorBoundary>
      </StatsigProviderWithSession>
    </CosmosProvider>
  );
}

export { App };
